import { CommandBar, Spinner, SpinnerSize } from 'office-ui-fabric-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SORTING_KEYS } from '../../../constants/sortingKeys';
import {
  getAllHearingsInitialize,
  getAllSubjectAreasInitialize,
} from '../../../features/actions/api';
import { isFetchingHearingsSelector } from '../../../features/apiFeatures/hearingSlice';
import {
  allSubjectAreaWithNumberOfHearingsSelector,
  isFetchingSubjectAreasSelector,
  numberOfHearingsOnSubjectAreaBySubjectAreaIdSelector,
  subjectAreaByIdSelector,
} from '../../../features/apiFeatures/subjectAreaSlice';
import { getAttr } from '../../../features/helpers/apiHelpers';
import {
  createSubjectArea,
  deleteSubjectArea,
  isCreatingSubjectAreaSelector,
  isPanelOpenSelector,
  isUpdatingSubjectAreaSelector,
  selectedSubjectAreaIdSelector,
  setIsPanelOpen,
  setSelectedSubjectAreaId,
  updateSubjectArea,
} from '../../../features/pageFeatures/subjectAreaAdministrationPageSlice';
import { comparators } from '../../../utilities/comparators';
import {
  filterAndSortingKeys,
  noSelectionKey,
} from '../../../utilities/filterAndSortingsUtil';
import { HeaderWithCommandBar } from './HeaderWithCommandBar';
import { CreateSubjectAreaPanel } from './subjectAreaAdministrationPage/CreateSubjectAreaPanel';
import { getCommandBarItems } from './subjectAreaAdministrationPage/getCommandBarItems';
import { mapSubjectArea } from './subjectAreaAdministrationPage/mapSubjectAreaItem';
import { SubjectAreaList } from './subjectAreaAdministrationPage/SubjectAreaList';

const filterSubjectAreas = (filter) => (subjectArea) =>
  filter === noSelectionKey ||
  (getAttr(subjectArea, 'name') &&
    getAttr(subjectArea, 'name').toLowerCase().includes(filter.toLowerCase()));

const commandBarStyles = {
  root: {
    backgroundColor: '#FAFAFA',
    paddingRight: '0px',
    Width: 'auto',
  },
};

const SubjectAreaAdministrationPage = () => {
  const dispatch = useDispatch();
  const { t: translator } = useTranslation();

  useEffect(() => {
    return function cleanup() {
      dispatch(getAllHearingsInitialize());
      dispatch(getAllSubjectAreasInitialize());
    };
  }, [dispatch]);

  const subjectAreas = useSelector(allSubjectAreaWithNumberOfHearingsSelector);
  const isFetchingSubjectAreas = useSelector(isFetchingSubjectAreasSelector);
  const isFetchingHearings = useSelector(isFetchingHearingsSelector);
  const selectedSubjectAreaId = useSelector(selectedSubjectAreaIdSelector);
  const isPanelOpen = useSelector(isPanelOpenSelector);
  const isCreatingSubjectArea = useSelector(isCreatingSubjectAreaSelector);
  const selectedSubjectArea = useSelector(
    subjectAreaByIdSelector(selectedSubjectAreaId),
  );
  const isUpdatingSubjectArea = useSelector(isUpdatingSubjectAreaSelector);
  const selectedSubjectAreaNumberOfHearings = useSelector(
    numberOfHearingsOnSubjectAreaBySubjectAreaIdSelector(selectedSubjectAreaId),
  );
  const [titleFilter, setTitleFilter] = useState(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [subjectAreaTitle, setSubjectAreaTitle] = useState('');
  const [filterAndSortingSelection, setFilterAndSortingSelection] = useState({
    [filterAndSortingKeys.sortingKey]: SORTING_KEYS.ALPHABETICAL,
    [filterAndSortingKeys.title]: noSelectionKey,
  });

  const onChangeSubjectAreaIsActive = (isActive) =>
    selectedSubjectAreaId &&
    dispatch(
      updateSubjectArea({
        subjectAreaId: selectedSubjectAreaId,
        isActive: isActive,
      }),
    );
  const openPanel = () => {
    setSubjectAreaTitle('');
    dispatch(setIsPanelOpen(true));
  };
  const onPanelClose = () => {
    setSubjectAreaTitle();
    dispatch(setIsPanelOpen(false));
  };
  const onDeleteSubjectArea = () => {
    selectedSubjectAreaId && dispatch(deleteSubjectArea(selectedSubjectAreaId));
  };
  const onConfirmCreateSubjectArea = () => {
    dispatch(createSubjectArea(subjectAreaTitle));
  };

  const filteredSubjectAreas = subjectAreas.filter(
    filterSubjectAreas(filterAndSortingSelection[filterAndSortingKeys.title]),
  );

  const itemsToRender = filteredSubjectAreas.map(mapSubjectArea(translator));

  const sortedItemsToRender = !!comparators.editSubjectAreas[
    filterAndSortingSelection[filterAndSortingKeys.sortingKey]
  ]
    ? itemsToRender.sort(
        comparators.editSubjectAreas[
          filterAndSortingSelection[filterAndSortingKeys.sortingKey]
        ],
      )
    : itemsToRender;

  const onSetTitleFilter = (value) => {
    setTitleFilter(value);

    let newState = Object.assign({}, filterAndSortingSelection);
    newState[filterAndSortingKeys.title] = value || noSelectionKey;
    setFilterAndSortingSelection(newState);
  };

  const onSearchBarClick = () => {
    setShowSearchBar(!showSearchBar);
    if (showSearchBar) {
      onSetTitleFilter(null, null);
    }
  };

  const selectedSubjectAreaIsActive = selectedSubjectArea
    ? getAttr(selectedSubjectArea, 'isActive')
    : null;

  const topCommandBarItems = getCommandBarItems(
    translator,
    onChangeSubjectAreaIsActive,
    selectedSubjectAreaIsActive,
    selectedSubjectAreaId,
    onDeleteSubjectArea,
    isUpdatingSubjectArea,
    selectedSubjectAreaNumberOfHearings,
    openPanel,
  );

  const isFetchingData = isFetchingSubjectAreas || isFetchingHearings;

  return (
    <div className="admin-page">
      <div className="admin-page-commandBar">
        <CommandBar items={topCommandBarItems} styles={commandBarStyles} />
      </div>
      <div className="admin-page-container">
        <HeaderWithCommandBar
          header={translator('pages.adminSubjectAreas.header')}
          titleFilterText={translator('pages.adminSubjectAreas.filterText')}
          showTitleFilter={showSearchBar}
          setShowTitleFilter={onSearchBarClick}
          titleFilter={titleFilter}
          setTitleFilter={onSetTitleFilter}
          disableGrid={true}
        />
        {isFetchingData ? (
          <Spinner size={SpinnerSize.large} />
        ) : (
          <SubjectAreaList
            subjectAreaItems={sortedItemsToRender}
            filterAndSortingSelection={filterAndSortingSelection}
            setFilterAndSortingSelection={setFilterAndSortingSelection}
            onSubjectAreaClick={(subjectAreaId) =>
              dispatch(setSelectedSubjectAreaId(subjectAreaId))
            }
            onSubjectAreaSelect={(subjectAreaId) =>
              dispatch(setSelectedSubjectAreaId(subjectAreaId))
            }
          />
        )}
        <CreateSubjectAreaPanel
          isOpen={isPanelOpen}
          onClose={onPanelClose}
          subjectAreaTitle={subjectAreaTitle}
          setSubjectAreaTitle={setSubjectAreaTitle}
          onConfirmCreateSubjectArea={onConfirmCreateSubjectArea}
          isCreatingSubjectArea={isCreatingSubjectArea}
        />
      </div>
    </div>
  );
};

export { SubjectAreaAdministrationPage };
